import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { SectionContext, SectionContextValue, useAccordionSection } from './PunchupAccordion';

// Lets ONE accordion section host multiple independently dirty-reporting forms (the General section:
// show details + tags) without their dirty/saving clobbering. Each panel gets an isolated context;
// the group OR-aggregates their dirty up to the real section so the orange-dot + navigation <Prompt>
// still fire for any unsaved panel. Full rationale + usage: PunchupAccordion/CLAUDE.md → "Sub-sections".

interface SubSectionRegistry {
	reportDirty: (subId: string, dirty: boolean) => void;
}

const SubSectionRegistryContext = createContext<SubSectionRegistry | null>(null);

/**
 * Wraps the body of a PunchupAccordionSection that hosts more than one independent Save-button form.
 * OR-aggregates its child PunchupAccordionSubSections' dirty state and reports it to the real section so
 * the orange-dot + navigation guard fire for any unsaved sub-panel. Must be rendered inside a section.
 */
export const PunchupAccordionSubSectionGroup: FC<{ children: ReactNode }> = ({ children }) => {
	const { setDirty } = useAccordionSection();
	const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

	const reportDirty = useCallback((subId: string, dirty: boolean) => {
		setDirtyMap((prev) => (prev[subId] === dirty ? prev : { ...prev, [subId]: dirty }));
	}, []);

	const anyDirty = Object.values(dirtyMap).some(Boolean);

	// Mirror useAccordionForm's effect shape: push the OR'd dirty to the real section, clear on unmount.
	useEffect(() => {
		setDirty(anyDirty);
		return () => setDirty(false);
	}, [anyDirty, setDirty]);

	// Stable so each panel's setDirty (useCallback dep on this value) stays stable across group re-renders —
	// otherwise useAccordionForm's [isDirty, setDirty] effect re-runs every render and briefly toggles the
	// panel's reported dirty off→on (a transient orange-dot / nav-guard drop).
	const registry = useMemo(() => ({ reportDirty }), [reportDirty]);

	return <SubSectionRegistryContext.Provider value={registry}>{children}</SubSectionRegistryContext.Provider>;
};

/**
 * One isolated panel inside a PunchupAccordionSubSectionGroup. Provides a fresh SectionContext whose
 * `setDirty`/`isSaving`/`setSaving` are local to this panel; `flash`/`close`/`scrollToSection`/`isOpen`
 * delegate to the enclosing real section. The panel's dirty is reported up to the group for aggregation.
 */
export const PunchupAccordionSubSection: FC<{ id: string; children: ReactNode }> = ({ id, children }) => {
	const parent = useAccordionSection();
	const registry = useContext(SubSectionRegistryContext);
	const [isSaving, setSaving] = useState(false);

	// useCallback so useAccordionForm's dirty-sync effect dep stays stable across renders.
	const setDirty = useCallback((dirty: boolean) => registry?.reportDirty(id, dirty), [registry, id]);

	const value: SectionContextValue = {
		close: parent.close,
		isOpen: parent.isOpen,
		flash: parent.flash,
		scrollToSection: parent.scrollToSection,
		setDirty,
		isSaving,
		setSaving,
	};

	return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
};
